import React, {useState, useMemo, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import {createRefetchContainer, graphql} from 'react-relay';

import {PageWrapper, Section, FlexRow, OutlineBox} from './InvoicingPage.styled';
import {StatBox, StatsContainer, StatTitle, StatValue} from '../../../styles/sections/insights/invoicing/invoicing_page_styled';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import * as tracking from '../../../tracking';
import {BUTTON_COLOR, BUTTON_STYLE, ELEMENT_TYPE, FILTER_SECTION, FILTER_TYPE} from '../../../constants';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/dropdown_section';
import Util from '../../../forecast-app/shared/util/util';
import CustomScrollDiv from '../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {useIntl, FormattedMessage} from 'react-intl';
import {getInitialOptions, handleChangedOptions} from './invoicing_eye_options';
import {getFilterFunctions} from '../../../forecast-app/shared/components/filters/filter_logic';
import {hideLoader, showLoader} from '../../../containers/global_loader';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {buildThirdLevelNavHeader} from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import {InvoicesTable} from './InvoicesTable';
import ContextMenu from '../../../forecast-app/shared/components/context-menus/context_menu';
import CreateInvoiceEntryMutation from '../../../mutations/create_invoice_entry_mutation';
import DeleteInvoiceEntryMutation from '../../../mutations/delete_invoice_entry_mutation';
import DeleteInvoiceMutation from '../../../mutations/delete_invoice_mutation';
import UnlinkInvoiceMutation from '../../../mutations/UnlinkInvoiceMutation';
import { useDataExport } from "../../../forecast-app/shared/hooks/useDataExport";
import InvoiceFormatter from "../../../forecast-app/shared/util/export-formatters/InvoiceFormatter";
import { hasFeatureFlag } from "../../../forecast-app/shared/util/FeatureUtil";

export const InvoicesPage = ({viewer, relay: {refetch}, onChangePage, invoiceMonth}) => {
	const STANDARD_MARGIN = 24;
	const LOCALSTORAGE_FILTERS_KEY = 'invoicing-invoices-filter';
	const LOCALSTORAGE_DATE_RANGE_KEY = 'invoices_date_range';
	const intl = useIntl();
	const history = useHistory();
	const initialDateRange = {
		startDate: invoiceMonth ? moment().month(invoiceMonth).startOf('month') : moment().subtract(30, 'day'),
		endDate: invoiceMonth ? moment().month(invoiceMonth).endOf('month') : moment(),
	};

	const hasIntegrations = viewer.company.newXeroEnabled || viewer.company.quickbooksEnabled || viewer.company.economicEnabled;
	const [theEyeOptions, setTheEyeOptions] = useState(getInitialOptions('invoices', hasIntegrations));
	const [dateRange, setDateRange] = useState(initialDateRange);
	const filters = localStorage.getItem(LOCALSTORAGE_FILTERS_KEY) ? JSON.parse(localStorage.getItem(LOCALSTORAGE_FILTERS_KEY)) : '';
	const [filterFunctions, setFilterFunctions] = useState(getFilterFunctions(filters));
	const [searchFilterValue, setSearchFilterValue] = useState('');
	const filteredInvoices = useMemo(() => viewer.invoicesV2.edges.map(edge => edge.node).filter(filterFunctions.invoiceFilter), [viewer.invoicesV2, filterFunctions.invoiceFilter]);
	const [contextMenuData, setContextMenuData] = useState(null);
	const [xeroTenantToShortCode, setXeroTenantToShortCode] = useState(null);

	const filterInvoicesByDateRange = invoices => {
		return invoices.filter(invoice => {
			// filter by date range
			const invoiceDate = Util.CreateNonUtcMomentDate(invoice.createdYear, invoice.createdMonth, invoice.createdDay);
			return invoiceDate.isBetween(dateRange.startDate, dateRange.endDate, 'day', '[]');
		});
	};

	const exportData = useDataExport(
		new InvoiceFormatter(intl, [], [], true),
		null,
		data => data,
		_ => `invoices_${dateRange.startDate.format('D-MMM-YYYY')}_${dateRange.endDate.format('D-MMM-YYYY')}`
	);

	const searchQueryFilteredInvoices = useMemo(() => {
		const filterInvoicesByDateRange1 = filterInvoicesByDateRange(filteredInvoices);
		return filterInvoicesByDateRange1.filter(invoice => {
			// filter by search criteria
			const nameMatch = invoice.name.toLowerCase().includes(searchFilterValue.toLowerCase());
			const clientMatch = invoice.client && invoice.client.name.toLowerCase().includes(searchFilterValue.toLowerCase());
			return nameMatch || clientMatch;
		});
	}, [filteredInvoices, searchFilterValue, dateRange]);

	const tableColumns = {
		invoiceId: true,
		invoiceName: true,
		client: theEyeOptions.find(eo => eo.name === 'client').checked,
		status: theEyeOptions.find(eo => eo.name === 'status').checked,
		date: theEyeOptions.find(eo => eo.name === 'invoiceDate').checked,
		invoicedAmount: theEyeOptions.find(eo => eo.name === 'total_invoiced').checked,
		paidAmount: theEyeOptions.find(eo => eo.name === 'paid').checked,
		unpaidAmount: theEyeOptions.find(eo => eo.name === 'unpaid').checked,
		dueDate: theEyeOptions.find(eo => eo.name === 'dueDate').checked,
		integrationLink: viewer.company.newXeroEnabled || viewer.company.quickbooksEnabled || viewer.company.economicEnabled,
		hasContextMenu: true,
	};

	const handleExportClick = (invoiceId, xeroId, quickbooksId, economicId) => {
		if (!xeroId && !quickbooksId && !economicId) {
			// Show invoice export modal

			// Xero
			if (viewer.company.newXeroEnabled) {
				tracking.trackEvent('Export to Xero pressed');
				showModal({
					type: MODAL_TYPE.XERO_EXPORT,
					invoiceId: invoiceId,
					xeroClients: viewer.company.clients.edges
						.filter(edge => edge.node.xeroContactId)
						.map(edge => ({value: edge.node.id, label: edge.node.name, xeroContactId: edge.node.xeroContactId}))
						.sort(function (a, b) {
							var labelA = a.label.toUpperCase();
							var labelB = b.label.toUpperCase();
							if (labelA < labelB) {
								return -1;
							}
							if (labelA > labelB) {
								return 1;
							}
							return 0;
						}),
				});
			}
			// E-conomic
			else if (viewer.company.economicEnabled) {
				tracking.trackEvent('Export to E-conomic pressed');
				showModal({
					type: MODAL_TYPE.ECONOMIC_EXPORT,
					invoiceId: invoiceId,
					economicCustomers: viewer.company.clients.edges
						.filter(edge => edge.node.economicCustomerId)
						.map(edge => ({value: edge.node.id, label: edge.node.name}))
						.sort(function (a, b) {
							var labelA = a.label.toUpperCase();
							var labelB = b.label.toUpperCase();
							if (labelA < labelB) {
								return -1;
							}
							if (labelA > labelB) {
								return 1;
							}
							return 0;
						}),
				});
			}
			// QuickBooks
			else if (viewer.company.quickbooksEnabled) {
				tracking.trackEvent('Export to QuickBooks pressed');
				showModal({
					type: MODAL_TYPE.QUICKBOOKS_EXPORT,
					invoiceId: invoiceId,
					quickbooksCustomers: viewer.company.clients.edges
						.filter(edge => edge.node.quickbooksCustomerId)
						.map(edge => ({value: edge.node.id, label: edge.node.name}))
						.sort(function (a, b) {
							var labelA = a.label.toUpperCase();
							var labelB = b.label.toUpperCase();
							if (labelA < labelB) {
								return -1;
							}
							if (labelA > labelB) {
								return 1;
							}
							return 0;
						}),
				});
			}
		}
	};

	// remove with feature flag new_csv_format
	const getInvoiceCSVData = (invoice, visibleColumns) => {
		let dataColumn = {};

		visibleColumns.forEach(column => {
			let value;

			const invoiceTotal = invoice.invoicedWithoutTax;
			const invoicePaid = invoice.paidWithoutTax;

			const currency = invoice.currency;

			switch (column) {
				case 'invoiceId':
					value = invoice.companyInvoiceId;
					break;
				case 'invoiceName':
					value = invoice.name;
					break;
				case 'client':
					value = invoice.client ? invoice.client.name : '';
					break;
				case 'status':
					value = invoice.status;
					break;
				case 'date':
					value = Util.CreateMomentDate(invoice.createdYear, invoice.createdMonth, invoice.createdDay).format('D MMM YYYY');
					break;
				case 'dueDate':
					value = Util.CreateMomentDate(invoice.dueYear, invoice.dueMonth, invoice.dueDay).format('D MMM YYYY');
					break;
				case 'invoicedAmount':
					value = invoiceTotal;
					break;
				case 'paidAmount':
					value = invoicePaid;
					break;
				case 'unpaidAmount':
					value = invoiceTotal - invoicePaid;
					break;
				case 'currency':
					value = currency;
					break;
			}

			dataColumn[column] = value;
		});

		return dataColumn;
	};

	// remove with feature flag new_csv_format
	const getCSVData = () => {
		let csvData = [];

		const visibleColumns = Object.keys(tableColumns).filter(columnName => tableColumns[columnName] && columnName !== 'hasContextMenu');
		visibleColumns.push('currency');

		searchQueryFilteredInvoices.forEach(invoice => {
			csvData.push(getInvoiceCSVData(invoice, visibleColumns));
		});

		return {csvData, visibleColumns};
	};

	const getHeader = () => {
		let leftContent = [],
			rightContent = [];

		const handleEyeOptionsSelect = (_, __, ___, newOptions) => {
			setTheEyeOptions(handleChangedOptions(newOptions, 'invoices'));
		};

		const handleCsvDownloadPress = () => {
			tracking.trackEvent('download invoices');

			if (hasFeatureFlag('new_csv_format', viewer.availableFeatureFlags)) {
				const visibleColumns = Object.keys(tableColumns).filter(columnName => tableColumns[columnName] && columnName !== 'hasContextMenu').map(name => {
					return {
						name: name,
						checked: true
					}
				});
				visibleColumns.push({ name: 'currency', checked: true });

				exportData(
					visibleColumns,
					searchQueryFilteredInvoices,
					null,
					null,
					'CSV'
				);
			} else {
				const {csvData, visibleColumns} = getCSVData();
				let exportData =
					visibleColumns.map(column =>
						column
							.split(/(?=[A-Z])/)
							.map(word => word.toLocaleLowerCase())
							.join(' ')
					) + '\r\n';
				exportData += Util.JSONToCSV(csvData);
				const fileName = `invoices_${dateRange.startDate.format('D-MMM-YYYY')}_${dateRange.endDate.format('D-MMM-YYYY')}.csv`;
				Util.exportToCSV(exportData, fileName);
			}
		};

		const onDateRangeChange = range => {
			setDateRange(range);
			Util.localStorageSetItem(LOCALSTORAGE_DATE_RANGE_KEY, JSON.stringify(range));
		};

		const onSearchChange = value => {
			tracking.trackEvent('Search invoices');
			setSearchFilterValue(value);
		};

		const onSyncButtonPress = () => {
			showLoader();
			tracking.trackEvent('Synchronize invoices');

			if (viewer.company.newXeroEnabled) {
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				const init = {headers: headers, credentials: 'include'};
				try {
					fetch(`${process.env.GRAPHQL_ENDPOINT.slice(0, -7)}xero/sync`, init)
						.then(result => result.json())
						.then(json => {
							window.location.reload();
							hideLoader();
						});
				} catch (error) {
					//TODO: Show error?
					hideLoader();
				}
			} else if (viewer.company.quickbooksEnabled) {
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				const init = {headers: headers, credentials: 'include'};
				try {
					fetch(`${process.env.GRAPHQL_ENDPOINT.slice(0, -7)}quickbooks/sync`, init)
						.then(result => result.json())
						.then(json => {
							window.location.reload();
							hideLoader();
						});
				} catch (error) {
					//TODO: Show error?
					hideLoader();
				}
			} else if (viewer.company.economicEnabled) {
				const headers = new Headers();
				headers.append('Content-Type', 'application/json');
				const init = {headers: headers, credentials: 'include'};
				try {
					fetch(`${process.env.GRAPHQL_ENDPOINT.slice(0, -7)}economic/sync`, init)
						.then(result => result.json())
						.then(json => {
							window.location.reload();
							hideLoader();
						});
				} catch (error) {
					//TODO: Show error?
					hideLoader();
				}
			}
		};

		const onNewInvoiceButtonPress = () => {
			tracking.trackEvent('Create new invoice modal');
			showModal({
				type: MODAL_TYPE.CREATE_INVOICE_V2_STANDALONE,
				refetch,
				changeTab: onChangePage,
				setDateRange,
				startDate: moment().subtract(2, 'month'),
				endDate: moment(),
			});
		};

		leftContent.push({
			type: ELEMENT_TYPE.THE_EYE,
			options: theEyeOptions,
			onSelect: handleEyeOptionsSelect,
			openRight: true,
			userpilot: 'eye-selector',
			cy: 'eye-selector',
		});

		leftContent.push({
			type: ELEMENT_TYPE.CSV,
			callback: handleCsvDownloadPress,
			userpilot: 'csv-button',
			cy: 'csv-button',
		});
		leftContent.push({
			type: ELEMENT_TYPE.DATE_RANGE_PICKER_WITH_QUICK_RANGES,
			defaultRangeItem: dateRange,
			onRangeItemChange: onDateRangeChange,
			clearable: false,
			userpilot: 'date-range-picker',
			calendarOffsetX: 10,
		});

		rightContent.push({
			type: ELEMENT_TYPE.SEARCH,
			placeholder: intl.formatMessage({id: 'invoicing.search_invoice_name'}),
			value: searchFilterValue || '',
			onChange: onSearchChange,
			userpilot: 'search-button',
			cy: 'search-button',
		});

		if (viewer.company.newXeroEnabled || viewer.company.quickbooksEnabled || viewer.company.economicEnabled) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: intl.formatMessage({id: 'common.synchronize'}),
				callback: onSyncButtonPress,
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				dataCy: 'synchronize-button',
			});
		}

		const onFilterChange = (_, filterFunctions) => {
			setFilterFunctions(filterFunctions);
		};

		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: FILTER_SECTIONS.INVOICES,
			invoiceFilters: [FILTER_TYPE.PROJECT, FILTER_TYPE.INVOICE_STATUS],
			filterSection: FILTER_SECTION.INVOICING_INVOICES,
			appliedFiltersName: `invoicing-invoices-filter`,
			viewer,
			onFiltersChange: onFilterChange,
		});

		rightContent.push({
			type: ELEMENT_TYPE.BUTTON,
			text: intl.formatMessage({id: 'invoicing.new_invoice'}),
			callback: onNewInvoiceButtonPress,
			style: BUTTON_STYLE.OUTLINE,
			color: BUTTON_COLOR.PURPLE,
			userpilot: 'new-invoice-button',
			dataCy: 'new-invoice-button',
		});

		return buildThirdLevelNavHeader(leftContent, rightContent, null, {parentGroup: null}, true);
	};

	const onEditInvoicePress = companyInvoiceId => {
		tracking.trackEvent('Edit invoice option pressed');
		history.push(`invoices/I-${companyInvoiceId}`);
	};

	const duplicateEntry = (entry, invoiceId) => {
		tracking.trackEvent('Duplicate entry');
		const {name, quantity, unitPrice, discount, tax, description, project, quickbooksItemOrService} = entry;
		const newEntry = {
			invoiceId: invoiceId,
			name,
			quantity,
			unitPrice,
			discount,
			tax,
			description,
			projectId: project.id,
			quickbooksItemOrService,
		};

		const onSuccess = () => {
			refetch();

			createToast({duration: 5000, message: intl.formatMessage({id: 'invoicing.entry_duplicated'})});
		};

		Util.CommitMutation(CreateInvoiceEntryMutation, newEntry, onSuccess);
	};

	const downloadEntry = entry => {
		tracking.trackEvent('Download entry');
		const {csvData, visibleColumns} = getCSVData(entry.id);
		let exportData =
			visibleColumns
				.filter(column => column !== 'hasContextMenu')
				.map(column =>
					column
						.split(/(?=[A-Z])/)
						.map(word => word.toLocaleLowerCase())
						.join(' ')
				) + '\r\n';
		exportData += Util.JSONToCSV(csvData);
		const fileName = `entry_${entry.name}.csv`;
		Util.exportToCSV(exportData, fileName);
	};

	const deleteEntry = (entry, refetch) => {
		tracking.trackEvent('Delete entry');
		const onSuccess = () => {
			refetch();

			createToast({duration: 5000, message: intl.formatMessage({id: 'invoicing.entry_deleted'})});
		};

		Util.CommitMutation(
			DeleteInvoiceEntryMutation,
			{
				id: entry.id,
			},
			onSuccess
		);
	};

	const deleteInvoice = (invoiceId, refetch) => {
		const onSuccess = () => {
			// update state
			refetch();

			createToast({duration: 5000, message: intl.formatMessage({id: 'invoice.invoice_deleted'})});
		};

		Util.CommitMutation(DeleteInvoiceMutation, {id: invoiceId}, onSuccess);
	};

	const unlinkInvoice = (invoiceId, refetch) => {
		const onSuccess = () => {
			// update state
			refetch();

			createToast({duration: 5000, message: intl.formatMessage({id: 'invoice.invoice_unlinked'})});
		};

		const callback = () => {
			Util.CommitMutation(UnlinkInvoiceMutation, {id: invoiceId}, onSuccess);
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'invoice.confirm_unlink_invoice',
			warningInformation: [<FormattedMessage id="invoice.unlink_warning" />],
			irreversible: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.unlink" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: callback,
				},
			],
		});
	};

	const getContextMenuOptions = (entry, invoiceId, companyInvoiceId, refetch) => {
		let options = [
			{
				text: intl.formatMessage({id: 'common.edit'}),
				onClick: () => onEditInvoicePress(companyInvoiceId),
			},
		];

		// this will be used when we will show entries on this page
		if (entry) {
			options.push(
				{
					text: intl.formatMessage({id: 'common.duplicate'}),
					onClick: () => duplicateEntry(entry, invoiceId),
				},
				{
					text: intl.formatNumberToPartsformatMessage({id: 'common.download'}),
					onClick: () => downloadEntry(entry),
				},
				{
					text: intl.formatMessage({id: 'common.delete'}),
					onClick: () => deleteEntry(entry, refetch),
					cy: 'delete-invoice-entry',
				}
			);
		} else {
			const invoice = viewer.invoicesV2.edges.find(invoice => invoice.node.id === invoiceId);
			options.push({
				text: intl.formatMessage({id: 'common.delete'}),
				onClick: () => deleteInvoice(invoiceId, refetch),
				disabled: invoice.node.status !== 'DRAFT' || invoice.node.xeroId || invoice.node.quickbooksId || invoice.node.economicId,
				cy: 'delete-invoice',
			});

			if (invoice.node.xeroId || invoice.node.quickbooksId || invoice.node.economicId) {
				options.push({
					text: intl.formatMessage({id: 'invoice.unlink_invoice'}),
					disabled: !viewer.company.integrations.userCanUnlinkInvoice,
					disabledDescription: 'Unlinking of invoices is not allowed. Contact an administrator',
					onClick: () => unlinkInvoice(invoiceId, refetch),
				});
			}
		}

		return options;
	};

	const handleOpenContextMenu = (e, invoiceId, companyInvoiceId) => {
		e.preventDefault();
		const newContextMenuData = {
			invoiceId,
			companyInvoiceId,
		};
		const btn = document.elementFromPoint(e.pageX, e.pageY);
		if (btn != null && e.type !== 'contextmenu') {
			const btnBounds = btn.getBoundingClientRect();
			newContextMenuData.posX = btnBounds.right - btn.clientWidth - 75;
			newContextMenuData.posY = btnBounds.y + 25;
		} else {
			newContextMenuData.posX = e.pageX;
			//check if there is place for menu underneath cursor
			if (window.innerHeight - e.pageY < 250) {
				newContextMenuData.posY = e.pageY - 250;
			} else {
				newContextMenuData.posY = e.pageY;
			}
		}
		setContextMenuData(newContextMenuData);
	};

	const handleCloseContextMenu = () => {
		setContextMenuData(null);
	};

	const totalInvoiced = filterInvoicesByDateRange(filteredInvoices).reduce((invoicesTotal, invoice) => invoicesTotal + invoice.invoicedWithoutTaxBaseCurrency, 0);
	const totalPaid = filterInvoicesByDateRange(filteredInvoices).reduce((invoicesPaidTotal, invoice) => invoicesPaidTotal + invoice.paidWithoutTaxBaseCurrency, 0);
	const totalUnpaid = totalInvoiced - totalPaid;

	const invoiceTableData = searchQueryFilteredInvoices.map((invoice, index) => {
		const isExported = !!(invoice.quickbooksId || invoice.xeroId || invoice.economicId);
		const {xeroId, xeroTenantId, quickbooksId, economicId, id, currency, status, createdYear, createdMonth, createdDay, dueYear, dueMonth, dueDay, name, companyInvoiceId, invoicedWithoutTax, paidWithoutTax, client, projects} = invoice;
		return {
			name,
			id,
			currency,
			companyInvoiceId,
			invoicedWithoutTax,
			paidWithoutTax,
			client,
			invoiceDate: Util.CreateMomentDate(createdYear, createdMonth, createdDay),
			dueDate: Util.CreateMomentDate(dueYear, dueMonth, dueDay),
			projects,
			status,
			xeroId,
			xeroTenantId,
			quickbooksId,
			economicId,
			quickbooksRealmId: viewer.company.quickbooksRealmId,
			isExported: isExported,
		};
	});

	useEffect(() => {
		if (viewer.company.newXeroEnabled && xeroTenantToShortCode == null) {
			const headers = new Headers();
			headers.append('Content-Type', 'application/json');
			const init = {headers: headers, credentials: 'include'};

			fetch(process.env.GRAPHQL_ENDPOINT.slice(0, -7) + 'xero/shortCodes', init)
				.then(result => result.json())
				.then(data => {
					setXeroTenantToShortCode(data);
				});
		}
	});

	return (
		<PageWrapper>
			{getHeader()}
			{!!contextMenuData && (
				<div style={{zIndex: 100}}>
					<ContextMenu
						options={getContextMenuOptions(undefined, contextMenuData.invoiceId, contextMenuData.companyInvoiceId, refetch)}
						contextMenuPosition={{x: contextMenuData.posX, y: contextMenuData.posY}}
						closeContextMenu={handleCloseContextMenu}
						userpilot={'context-menu-wrapper'}
					/>
				</div>
			)}
			<CustomScrollDiv>
				<Section sticky marginLeft={STANDARD_MARGIN} marginRight={STANDARD_MARGIN} data-userpilot={'invoices-totals'}>
					<StatsContainer growable columns={3} data-userpilot={'invoices-totals'}>
						<StatBox>
							<StatTitle>{intl.formatMessage({id: 'common.invoiced'})}</StatTitle>
							<StatValue data-cy={'total-invoiced'}>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(viewer.company.currency), totalInvoiced, intl)}</StatValue>
						</StatBox>
						<StatBox>
							<StatTitle>{intl.formatMessage({id: 'settings_subscription.paid'})}</StatTitle>
							<StatValue data-cy={'total-paid'}>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(viewer.company.currency), totalPaid, intl)}</StatValue>
						</StatBox>
						<StatBox>
							<StatTitle>{intl.formatMessage({id: 'settings_subscription.unpaid'})}</StatTitle>
							<StatValue data-cy={'total-unpaid'}>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(viewer.company.currency), totalUnpaid, intl)}</StatValue>
						</StatBox>
					</StatsContainer>
				</Section>
				<Section marginBottom={55} data-userpilot={'invoices-table'}>
					<FlexRow growable>
						<OutlineBox growable marginRight={STANDARD_MARGIN} marginLeft={STANDARD_MARGIN} marginBottom={STANDARD_MARGIN}>
							<InvoicesTable
								onExportClick={handleExportClick}
								data={invoiceTableData}
								totals={{totalInvoiced, totalPaid, totalUnpaid}}
								theEyeOptions={theEyeOptions}
								companyCurrency={viewer.company.currency}
								handleOpenContextMenu={handleOpenContextMenu}
								onEditInvoice={onEditInvoicePress}
								integrationSettings={{xeroTenantToShortCode}}
							/>
						</OutlineBox>
					</FlexRow>
				</Section>
			</CustomScrollDiv>
		</PageWrapper>
	);
};

const invoicesPageQuery = graphql`
	query InvoicesPageQuery {
		viewer {
			actualPersonId
			component(name: "invoicing_list_page")
			...InvoicesPage_viewer
		}
	}
`;

export {invoicesPageQuery};

export default createRefetchContainer(
	InvoicesPage,
	{
		viewer: graphql`
			fragment InvoicesPage_viewer on Viewer {
				id
				filters(first: 1000000, filterSection: INVOICING_INVOICES) @connection(key: "Viewer_filters", filters: []) {
					edges {
						node {
							id
							name
							section
							value
						}
					}
				}
				company {
					currency
					newXeroEnabled
					quickbooksEnabled
					quickbooksRealmId
					economicEnabled
					newXeroEnabled
					xeroShortCode
					clients {
						edges {
							node {
								id
								name
								xeroContactId
								economicCustomerId
								quickbooksCustomerId
							}
						}
					}
					integrations {
						userCanUnlinkInvoice
					}
				}
				invoicesV2(first: 100000) @connection(key: "Viewer_invoicesV2") {
					edges {
						node {
							id
							companyInvoiceId
							name
							status
							currency
							hasEntriesWithNoProject
							invoicedWithoutTax
							invoicedWithoutTaxBaseCurrency
							paidWithoutTax
							paidWithoutTaxBaseCurrency
							createdYear
							createdMonth
							createdDay
							dueYear
							dueMonth
							dueDay
							xeroId
							xeroTenantId
							quickbooksId
							economicId
							client {
								id
								name
							}
							projects {
								id
								companyProjectId
								name
								projectColor
							}
						}
					}
				}
			}
		`,
	},
	graphql`
		query InvoicesPageRefetchQuery {
			viewer {
				...InvoicesPage_viewer
			}
		}
	`
);
